import { hasApiErrorCode, unwrapApiError } from '@/utils/apiError'

/**
 * Guards de erro das mutações de plantão (create/update/delete de Station),
 * compartilhados pelos modais de criação e edição do Dashboard.
 *
 * Segue o padrão de `src/modules/company/PostStation/errors.ts`: guards que
 * recebem `unknown` (o parâmetro de um `catch` sob `strict`) e fazem a
 * narrowing internamente, chamáveis direto no call site sem re-cast.
 */

/** Código do bloqueio de duração: plantão não pode exceder o limite configurado. */
export const STATION_DURATION_ERROR_CODE = 'STATION_DURATION_GREATER_THAN_ALLOWED'

/**
 * True quando o backend rejeitou a mutação porque a duração do plantão excede
 * o limite permitido. O backend publica o código em `errorCode`, com uma frase
 * pronta em pt-BR em `message` (que cita o limite real); `hasApiErrorCode`
 * cobre `code`, `errorCode` e o código cru em `message`.
 */
export function isStationDurationExceeded(error: unknown): boolean {
  return hasApiErrorCode(error, STATION_DURATION_ERROR_CODE)
}

/**
 * Código estruturado da trava de check-in, publicado pelo backend no campo
 * `errorCode` (catálogo `src/modules/stations/domain/stations-errors.ts`).
 */
export const STATION_CHECK_IN_LOCK_CODE = 'DOCTOR_STATION_ALREADY_HAS_CHECK_IN'

/**
 * True quando o backend bloqueou a edição/remoção porque o plantão já possui
 * check-in. Detecção exclusivamente por código (`code`/`errorCode`/código cru
 * em `message`): o texto da resposta não é contrato e varia por endpoint.
 */
export function isStationCheckInLocked(error: unknown): boolean {
  return hasApiErrorCode(error, STATION_CHECK_IN_LOCK_CODE)
}

/**
 * Frase a exibir para a trava de check-in/check-out: a do backend quando ela
 * vem (o backend manda texto pronto em pt-BR, específico por contexto — editar,
 * excluir, excluir em massa, transferir), senão o `fallback` do call site.
 *
 * Mesmo padrão de `getPostStationApprovedMessage`: repassar em vez de reescrever.
 * O código cru em `message` (contrato legado) nunca é exibido.
 */
export function getStationCheckInLockMessage(error: unknown, fallback: string): string {
  return getBackendMessageUnlessCode(error, STATION_CHECK_IN_LOCK_CODE, fallback)
}

/**
 * Frase a exibir para o bloqueio de duração: a do backend quando ela vem (cita o
 * limite real configurado), senão o `fallback` do call site. Mesmo padrão de
 * `getStationCheckInLockMessage`: o código cru em `message` nunca é exibido.
 */
export function getStationDurationExceededMessage(error: unknown, fallback: string): string {
  return getBackendMessageUnlessCode(error, STATION_DURATION_ERROR_CODE, fallback)
}

function getBackendMessageUnlessCode(error: unknown, code: string, fallback: string): string {
  const err = unwrapApiError(error) as
    | { response?: { data?: { message?: unknown } } }
    | null
    | undefined
  // eslint-disable-next-line no-restricted-syntax -- helper de domínio sobre o erro já desembrulhado
  const message = err?.response?.data?.message
  if (typeof message !== 'string') return fallback
  const trimmed = message.trim()
  return trimmed && trimmed !== code ? trimmed : fallback
}
